import React, { forwardRef, useState, useContext } from "react";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";

import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";

import {
  formatQueryMetric,
  formatDate,
} from "components/QueryBuilder/Utils/QueryBuilderUtils";

import { formatDataForTable } from "components/Utils/ChartsDataUtils";

const QueryBuilderTable = forwardRef((props, ref) => {
  const { state: globalState } = useContext(QueryBuilderContext);

  // Table
  const { defaultTableData } = formatDataForTable(
    props.data,
    props.dimensions,
    props.queryMetrics
  );

  // DATE | Group By    |Minutes Viewed    |Unique Viewers    |Min/Viewer|
  // ----|----|----|----|----
  // 1-Aug    Android    7,604    1000     7.60
  // 1-Aug    Roku    61,190    5000     12.24
  // 1-Aug    Unknown    3,112    1000     3.11
  // 1-Aug    Web    42,428    8000     5.30
  // 1-Aug    IOS    32,475    3000     10.83
  // 1-Aug    TvOS    23,826    3500     6.81
  // 2-Aug    Android    7,604    1000     7.60
  // 2-Aug    Roku    61,190    5000     12.24
  // 2-Aug    Unknown    3,112    1000     3.11
  // 2-Aug    Web    42,428    8000     5.30
  // 2-Aug    IOS    32,475    3000     10.83
  // 2-Aug    TvOS    23,826    3500     6.81

  // const _data = {
  //   metrics: [
  //     {name: "metric", title: "Views"},
  //     {name: "metric2", title: "Video"},
  //     {name: "dimension1", title: "Platform"}
  //   ],
  //   data: [
  //     { date: '19-09-2019', metric: "Views", metric2: "some video", dimension1: "US", value: 123},
  //     { date: '19-09-2019', metric: "Views", metric2: "some video", dimension1: "CA", value: 234},
  //     { date: '19-09-2019', metric: "Views", metric2: "some video 2", dimension1: "US", value: 345},
  //     { date: '19-09-2019', metric: "Views", metric2: "some video 2", dimension1: "CA", value: 456},
  //     { date: '19-09-2019', metric: "Video Views", metric2: "some video", dimension1: "US", value: 321},
  //     { date: '19-09-2019', metric: "Video Views", metric2: "some video", dimension1: "CA", value: 432}
  //   ]
  // };

  // fill metrics
  const metrics = [];
  metrics.push({ name: "date", title: "Date" });
  metrics.push({ name: "metric", title: "Metric" });
  props.dimensions.forEach((dim) => metrics.push(dim));
  metrics.push({ name: "value", title: "Value" });

  const granularity = globalState.queryOptions.granularity;

  // add unique ids
  defaultTableData.forEach((d, i) => (d.id = i));

  // get cleaned data
  const getData = (_data) => {
    return _data.sort((a, b) => {
      // date
      if (a.date > b.date) return 1;
      if (a.date < b.date) return -1;

      // metric
      if (a.metric > b.metric) return 1;
      if (a.metric < b.metric) return -1;

      // dimensions
      let order = 0;
      props.dimensions.forEach((dim) => {
        if (a[dim.name] > b[dim.name]) {
          if (order === 0) order = 1;
        }
        if (a[dim.name] < b[dim.name]) {
          if (order === 0) order = -1;
        }
      });
      if (order !== 0) return order;

      // value
      if (a.value >= b.value) return -1;
      if (a.value < b.value) return 1;

      return 1;
    });
  };

  const [data] = useState(getData(defaultTableData));

  const generateTableColumns = (metrics) => {
    const columns = [];

    metrics.forEach((metric) => {
      const column = {
        dataField: metric.name,
        text: metric.title,
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        sort: true,
        // onSort: (field, order) => customSort(field, order),
        // sortFunc: () => true
      };
      if (column.dataField === "date") {
        const format = formatDate(granularity);

        column.formatter = (cell) =>
          moment(new Date(cell)).utc().format(format);
      }

      if (column.dataField === "value") {
        column.formatter = (cell, row) =>
          formatQueryMetric(
            row.metric_id,
            cell,
            globalState.queryMetrics,
            "table"
          );
      }
      columns.push(column);
    });
    return columns;
  };

  const columns = generateTableColumns(metrics);

  const pagination = paginationFactory({
    sizePerPage: 20,
    hideSizePerPage: true,
  });

  const filename =
    (props.title && props.title.split(" ").join("_")) || "spreadsheet";

  return (
    <div className="row">
      <div className="col-12 query-view-border">
        <ToolkitProvider
          keyField="id"
          data={data}
          columns={columns}
          striped={false}
          hover={false}
          bordered={false}
          exportCSV={{
            fileName: filename + ".csv",
            exportAll: false,
            onlyExportFiltered: true,
          }}
        >
          {(props) => (
            <BootstrapTable
              {...props.baseProps}
              {...props.csvProps}
              pagination={pagination}
              ref={ref}
            />
          )}
        </ToolkitProvider>
      </div>
    </div>
  );
});

export default QueryBuilderTable;
